import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { CurrentUser } from './auth.model';
import { AuthStateService } from './auth-state.service';

interface CsrfTokenResponse {
  headerName: string;
  parameterName: string;
  token: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly state = inject(AuthStateService);

  private restoreRequest: Observable<boolean> | null = null;

  login(username: string, password: string): Observable<CurrentUser> {
    const normalizedUsername = username.trim();

    if (!normalizedUsername || !password) {
      return throwError(() => new Error('Informe usuário e senha.'));
    }

    const body = new HttpParams()
      .set('username', normalizedUsername)
      .set('password', password);

    return this.ensureCsrfToken().pipe(
      switchMap(() => this.http.post<void>('/api/auth/login', body)),
      /*
       * O Spring Security invalida o token CSRF anterior após autenticar.
       * Pedimos um novo token antes de qualquer escrita subsequente.
       */
      switchMap(() => this.ensureCsrfToken()),
      switchMap(() => this.loadCurrentUser()),
      tap((user) => this.state.setAuthenticated(user)),
    );
  }

  certificadoDisponivel(): Observable<boolean> {
    return this.http.get<{ habilitado: boolean }>('/api/auth/certificado/disponibilidade').pipe(
      map(resposta => resposta.habilitado),
    );
  }

  prepararLoginCertificado(): Observable<string> {
    return this.ensureCsrfToken().pipe(
      switchMap(() => this.http.post<{ url: string }>('/api/auth/certificado/preparar', {})),
      map(resposta => resposta.url),
    );
  }

  concluirLoginCertificado(ticket: string): Observable<CurrentUser> {
    return this.ensureCsrfToken().pipe(
      switchMap(() => this.http.post<void>('/api/auth/certificado/concluir', { ticket })),
      switchMap(() => this.ensureCsrfToken()),
      switchMap(() => this.loadCurrentUser()),
      tap(user => this.state.setAuthenticated(user)),
    );
  }

  requestPasswordRecovery(identifier: string): Observable<void> {
    return this.ensureCsrfToken().pipe(
      switchMap(() =>
        this.http.post<void>('/api/auth/password-recovery/request', {
          identificador: identifier.trim(),
        }),
      ),
    );
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.ensureCsrfToken().pipe(
      switchMap(() =>
        this.http.post<void>('/api/auth/password-recovery/reset', {
          token,
          novaSenha: newPassword,
        }),
      ),
    );
  }

  logout(): Observable<void> {
    return this.ensureCsrfToken().pipe(
      switchMap(() => this.http.post<void>('/api/auth/logout', null)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return of(void 0);
        }
        return throwError(() => error);
      }),
      finalize(() => this.state.setAnonymous()),
    );
  }

  ensureSession(force = false): Observable<boolean> {
    if (!force && this.state.status() !== 'unknown') {
      return of(this.state.authenticated());
    }

    if (this.restoreRequest) {
      return this.restoreRequest;
    }

    const request = this.loadCurrentUser().pipe(
      tap((user) => this.state.setAuthenticated(user)),
      /*
       * Também garante que um F5 com sessão ainda válida tenha um token
       * XSRF disponível para POST/PUT/DELETE.
       */
      switchMap(() => this.ensureCsrfToken()),
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        this.state.setAnonymous();

        if (error.status === 401 || error.status === 403) {
          return of(false);
        }

        return of(false);
      }),
      finalize(() => {
        this.restoreRequest = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.restoreRequest = request;
    return request;
  }

  refreshCurrentUser(): Observable<CurrentUser> {
    return this.loadCurrentUser().pipe(
      tap((user) => this.state.setAuthenticated(user)),
    );
  }

  private loadCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>('/api/auth/me');
  }

  private ensureCsrfToken(): Observable<void> {
    return this.http.get<CsrfTokenResponse>('/api/auth/csrf').pipe(
      map(() => void 0),
    );
  }
}
